import type {
  ExploreInterpretResponse,
  ExploreStep,
  InterpretationOption,
} from '@/message-analyzer/types/explore'

export const EXPLORE_SESSION_KEY = 'lina_explore_session_v1'
const STORAGE_KEY = EXPLORE_SESSION_KEY

export type ExplorePendingAction = 'coaching' | null

export interface ExploreSessionState {
  version: 1
  savedAt: string
  /** auth user / account id when the explore analysis was created */
  accountId?: string | null
  step: ExploreStep
  messages: { id: string; text: string }[]
  selectedRelationship: string
  customRelationship: string
  additionalContext: string
  exploreData: ExploreInterpretResponse | null
  selectedInterpretation: InterpretationOption | null
  pendingAction: ExplorePendingAction
  /** Response coaching side panel open (analysis stays visible). */
  coachingPanelOpen?: boolean
}

export function createEmptyExploreSession(): ExploreSessionState {
  return {
    version: 1,
    savedAt: new Date().toISOString(),
    step: 'input',
    messages: [{ id: '1', text: '' }],
    selectedRelationship: '',
    customRelationship: '',
    additionalContext: '',
    exploreData: null,
    selectedInterpretation: null,
    pendingAction: null,
    coachingPanelOpen: false,
  }
}

export function loadExploreSession(): ExploreSessionState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ExploreSessionState
    if (parsed?.version !== 1) return null
    return parsed
  } catch {
    return null
  }
}

export function saveExploreSession(state: ExploreSessionState): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...state, savedAt: new Date().toISOString() }),
    )
  } catch (err) {
    console.warn('[explore-session] Failed to persist session', err)
  }
}

export function clearExploreSession(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function hasExploreResumePending(): boolean {
  const session = loadExploreSession()
  return (
    session?.pendingAction === 'coaching' &&
    !!session.exploreData &&
    !!session.selectedInterpretation
  )
}

export function getExploreResumePath(): string {
  return '/analyze'
}

export { resolvePostAuthDestination } from '@/message-analyzer/lib/guest-coaching-resume'

/**
 * True only when we can safely PATCH the learning_signals row for this session.
 * Returns false after login with a different account, or when session owner is unknown.
 */
export function shouldSyncExploreLearningSignal(
  sessionAccountId: string | null | undefined,
  currentAccountId: string | null | undefined,
): boolean {
  if (!currentAccountId || !sessionAccountId) return false
  return sessionAccountId === currentAccountId
}
